import { useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../i18n/useI18n';
import {
  formatCalendarDay,
  formatCalendarMonth,
  lastDayOfMonth,
} from '../../utils/dateTimeUtils';
import type { CostChartViewMode } from './costChartTypes';

/** @returns Inclusive range with `fromDay` / `toDay` as `YYYY-MM-DD`. */
function getDailyRange(
  year: number,
  month: number,
): { fromDay: string; toDay: string } {
  const now = new Date();
  const isCurrentMonth =
    year === now.getFullYear() && month === now.getMonth() + 1;
  const lastDay = isCurrentMonth ? now.getDate() : lastDayOfMonth(year, month);
  return {
    fromDay: formatCalendarDay(year, month, 1),
    toDay: formatCalendarDay(year, month, lastDay),
  };
}

/** @returns Inclusive range with `fromMonth` / `toMonth` as `YYYY-MM`. */
function getMonthlyRange(year: number): { fromMonth: string; toMonth: string } {
  const now = new Date();
  const isCurrentYear = year === now.getFullYear();
  return {
    fromMonth: formatCalendarMonth(year, 1),
    toMonth: formatCalendarMonth(year, isCurrentYear ? now.getMonth() + 1 : 12),
  };
}

export function useCostChartRange() {
  const { locale } = useI18n();
  const now = useMemo(() => new Date(), []);
  const [mode, setMode] = useState<CostChartViewMode>('daily');
  const [dailyMonth, setDailyMonth] = useState(() => ({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  }));
  const [monthlyYear, setMonthlyYear] = useState(() => now.getFullYear());

  const isCurrentMonth =
    dailyMonth.year === now.getFullYear() &&
    dailyMonth.month === now.getMonth() + 1;
  const isCurrentYear = monthlyYear === now.getFullYear();

  const { fromDay, toDay } = useMemo(
    () => getDailyRange(dailyMonth.year, dailyMonth.month),
    [dailyMonth.year, dailyMonth.month],
  );
  const { fromMonth, toMonth } = useMemo(
    () => getMonthlyRange(monthlyYear),
    [monthlyYear],
  );

  const prevDaily = useCallback(() => {
    setDailyMonth((prev) =>
      prev.month === 1
        ? { year: prev.year - 1, month: 12 }
        : { ...prev, month: prev.month - 1 },
    );
  }, []);
  const nextDaily = useCallback(() => {
    if (isCurrentMonth) return;
    setDailyMonth((prev) =>
      prev.month === 12
        ? { year: prev.year + 1, month: 1 }
        : { ...prev, month: prev.month + 1 },
    );
  }, [isCurrentMonth]);

  const prevMonthly = useCallback(() => {
    setMonthlyYear((year) => year - 1);
  }, []);
  const nextMonthly = useCallback(() => {
    if (isCurrentYear) return;
    setMonthlyYear((year) => year + 1);
  }, [isCurrentYear]);

  const rangeLabel = useMemo(() => {
    if (mode === 'daily') {
      return new Date(
        dailyMonth.year,
        dailyMonth.month - 1,
        1,
      ).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
    return String(monthlyYear);
  }, [mode, dailyMonth, monthlyYear, locale]);

  const canGoNext = mode === 'daily' ? !isCurrentMonth : !isCurrentYear;
  const goPrev = mode === 'daily' ? prevDaily : prevMonthly;
  const goNext = mode === 'daily' ? nextDaily : nextMonthly;

  return {
    mode,
    setMode,
    fromDay,
    toDay,
    fromMonth,
    toMonth,
    rangeLabel,
    canGoNext,
    goPrev,
    goNext,
  };
}

export type CostChartRange = ReturnType<typeof useCostChartRange>;
